import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { canAccessAdmin as canAccessAdminByRole, canAccessLearner as canAccessLearnerByRole, getLcAdminHomePath, hasLcPermission } from '@/utils/roleUtils'
import { resolvePostLoginPath, type LoginEntry } from '@/utils/authRedirect'
import { useTenantStore } from '@/store/tenantStore'
import { lazy, Suspense, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Toaster, toast } from 'sonner'
import { isDevToolsVisible, QuizModeProvider } from '@quebon/shared-learning'

// Layouts
import HandheldCoachingPage from '@/pages/admin/HandheldCoachingPage'
import { handheldLoginReturn } from '@/components/admin/coaching/handheld/handheldNavigation'
import AdminLayout from '@/components/layout/AdminLayout'
import LearningLayout from '@/components/layout/LearningLayout'
import HomeLayout from '@/components/layout/HomeLayout'
import ScrollToTop from '@/components/common/ScrollToTop'
import { MallOperationGuard } from '@/components/auth/MallOperationGuard'
import { TestCenterGuard } from '@/components/auth/TestCenterGuard'

// Auth Pages
import LoginPage from '@/pages/auth/LoginPage'
import AdminLoginPage from '@/pages/auth/AdminLoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import SelfRegisterPage from '@/pages/auth/SelfRegisterPage'

// Home (Landing) Pages
import MainLandingPage from '@/pages/home/MainLandingPage'
import BrochurePage from '@/pages/home/BrochurePage'
import AboutQuebonPage from '@/pages/home/AboutQuebonPage'
import AboutLcPage from '@/pages/home/AboutLcPage'
import FranchisePage from '@/pages/home/FranchisePage'
import ReviewsPage from '@/pages/home/ReviewsPage'
import AnnouncementsPage from '@/pages/home/AnnouncementsPage'
import AnnouncementDetailPage from '@/pages/home/AnnouncementDetailPage'

// Admin Pages (LC 전용)
import AdminDashboard from '@/pages/admin/AdminDashboard'
import ClassManagement from '@/pages/admin/ClassManagement'
import OrdersPage from '@/pages/admin/OrdersPage'
import MallPage from '@/pages/admin/MallPage'
import StudentManagement from '@/pages/admin/StudentManagement'
import LcProductManagement from '@/pages/admin/LcProductManagement'
import ProductDetail from '@/pages/admin/ProductDetail'
import ProgressReport from '@/pages/admin/ProgressReport'
import NotesPage from '@/pages/admin/NotesPage'
import DeviceManagement from '@/pages/admin/DeviceManagement'
import AdminAnnouncements from '@/pages/admin/AdminAnnouncements'
import AiqOrgReport from '@/pages/admin/AiqOrgReport'
import CoachingPage from '@/pages/admin/CoachingPage'
import StudentDetail from '@/pages/admin/StudentDetail'
import StudentAiqReport from '@/pages/admin/DiagnosticAiqReportPages'
import StudentDiagnosticReport from '@/pages/admin/StudentDiagnosticReport'
import ContentManagement from '@/pages/admin/ContentManagement'
import ContentLessonDetailPage from '@/pages/admin/ContentLessonDetailPage'
import AdminGameMathPlayer from '@/pages/admin/AdminGameMathPlayer'
import PerformanceManagement from '@/pages/admin/PerformanceManagement'
import Inquiries from '@/pages/admin/Inquiries'
import LibraryPage from '@/pages/admin/LibraryPage'
import NotificationSettings from '@/pages/admin/settings/NotificationSettings'
import CenterInfoPage from '@/pages/admin/settings/CenterInfo'
import CenterAccountsSettings from '@/pages/admin/settings/CenterAccountsSettings'
import AdminProfile from '@/pages/admin/AdminProfile'
import CoachingPrintPage, { DiagnosticSetPrintPage } from '@/pages/admin/CoachingPrintPage'
import TestReportPrintPage from '@/pages/admin/TestReportPrintPage'
import ChapterReportEditPage from '@/pages/admin/ChapterReportEditPage'
import ChapterReportPrintPage from '@/pages/admin/ChapterReportPrintPage'
import CumulativeAssessmentReportPage from '@/pages/admin/CumulativeAssessmentReportPage'
import TrialAnalyticsPage from '@/pages/admin/TrialAnalyticsPage'

// Learner Layout & Pages
import LearnerLayout from '@/components/layout/LearnerLayout'
import { LearnerLoading } from '@/components/learner/ui/LearnerLoading'
import { useAttendanceWatcher } from '@/hooks/learner/useAttendanceWatcher'
import TodayClass from '@/pages/learner/TodayClass'
import Library from '@/pages/learner/Library'
import Playground from '@/pages/learner/Playground'
import PlaygroundGame from '@/pages/learner/PlaygroundGame'
import MyProfile from '@/pages/learner/MyProfile'
import AiqTest from '@/pages/learner/AiqTest'
import DesignSystemDemo from '@/pages/learner/DesignSystemDemo'
import CumulativeNoticePreview from '@/pages/learner/CumulativeNoticePreview'

// Dev Test Pages
import QuizTestPage from '@/pages/learner/QuizTestPage'
import AiqTestPage from '@/pages/learner/AiqTestPage'
import ToolTestPage from '@/pages/learner/ToolTestPage'
import QuetureTestPage from '@/pages/learner/QuetureTestPage'
import GameTestPage from '@/pages/learner/GameTestPage'

// Device
import DeviceActivatePage from '@/pages/device/DeviceActivatePage'
import PhotoUploadPage from '@/pages/photo/PhotoUploadPage'
import GrowthAlertPublicPage from '@/pages/public/GrowthAlertPublicPage'
import TenantNotFoundPage from '@/pages/public/TenantNotFoundPage'

// Learn page (fullscreen learning mode)
import LearnPage from '@/pages/learner/LearnPage'

// Library review pages
import LibraryLessonReview from '@/pages/learner/LibraryLessonReview'
import LibraryStepPlayer from '@/pages/learner/LibraryStepPlayer'

// Diagnostic (진단테스트)
import { useDiagnosticFlow } from '@/hooks/learner/useDiagnosticFlow'
import { useAttendanceAccess } from '@/hooks/learner/useAttendanceAccess'
import { usePreviewGate } from '@/hooks/learner/usePreviewGate'
import { isLearnerRoutePath } from '@/hooks/learner/learnerRouteGuards'
import MyIndexRedirect from '@/components/learner/MyIndexRedirect'
import DiagnosticAiq from '@/pages/learner/diagnostic/DiagnosticAiq'
import DiagnosticAiqComplete from '@/pages/learner/diagnostic/DiagnosticAiqComplete'
import MathHeadAssessment from '@/pages/learner/cumulative/MathHeadAssessment'

// Special Lecture (단원평가 특강)
import { useSpecialLectureFlow } from '@/hooks/learner/useSpecialLectureFlow'
import SpecialLectureOnline from '@/pages/learner/special-lecture/SpecialLectureOnline'
import SpecialLectureReview from '@/pages/learner/special-lecture/SpecialLectureReview'
import SpecialLectureWait from '@/pages/learner/special-lecture/SpecialLectureWait'

const CoachingNewPage = lazy(() => import('@/pages/admin/CoachingNewPage'))
const CoachingAltPage = lazy(() => import('@/pages/admin/CoachingAltPage'))

/**
 * 학습자 자동 로그아웃 watcher 전역 마운트.
 * 라우트(탭바/레슨/시험 등)와 무관하게 인증된 학습자 세션 내내 단 한 번만 떠 있어야
 * 폴링이 끊기지 않고(①) prevRef 리셋으로 인한 전이 소실(②)도 없다. 렌더는 없음.
 */
function AttendanceWatcher() {
  useAttendanceWatcher()
  return null
}

function ProtectedRoute({ children, adminOnly = false, ownerOnly = false, requiredPermission }: { children: React.ReactNode, adminOnly?: boolean, ownerOnly?: boolean, requiredPermission?: string }) {
  const { isAuthenticated, user, isLoading, hasCheckedAuth, postLogoutAllowedPath } = useAuthStore()
  const location = useLocation()
  const isLearnerPath = !adminOnly && isLearnerRoutePath(location.pathname)
  const isLearnerContext = isLearnerPath && canAccessLearnerByRole(user?.role)
  const isSpecialLectureRoute = location.pathname.startsWith('/my/special-lecture/')
  const attendanceAccess = useAttendanceAccess({ enabled: isLearnerContext })
  const learningFlowEnabled =
    isLearnerContext && !attendanceAccess.isLoading && !attendanceAccess.isRestricted
  // React Hook 규칙: 모든 hook은 early return 이전에 호출. path-aware hook이라
  // 로딩/미인증 시점에도 안전하게 null 반환.
  const diagnosticFlow = useDiagnosticFlow({ enabled: learningFlowEnabled })
  const specialLectureFlow = useSpecialLectureFlow({ enabled: learningFlowEnabled })
  const previewGate = usePreviewGate({ enabled: isLearnerContext })
  const { t } = useTranslation('nav')

  // 콘텐츠 사용료 게이트(NONE/NOT_STARTED/EXHAUSTED/EXPIRED)로 학습 라우트가 막혀 프리뷰 홈으로
  // redirect될 때, 아무 안내 없이 튕기지 않도록 사유별 toast를 띄운다.
  // (대기/휴원/퇴원='STATUS'는 탭 잠금 UI가 안내하므로 제외)
  const feeGateReason =
    previewGate && previewGate.reason !== 'STATUS' ? previewGate.reason : null
  useEffect(() => {
    if (feeGateReason) toast.info(t(`preview.feeGate.${feeGateReason}`))
  }, [feeGateReason, t])

  if (!hasCheckedAuth || isLoading || (isLearnerContext && attendanceAccess.isLoading)) {
    if (isLearnerPath) return <LearnerLoading />

    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block size-8 rounded-full border-b-2 animate-spin border-primary-600"></div>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    if (!adminOnly && postLogoutAllowedPath === location.pathname) {
      return <>{children}</>
    }
    if (handheldLoginReturn(location.pathname + location.search)) {
      return <Navigate to={`/admin/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />
    }
    return <Navigate to="/login" replace />
  }

  if (adminOnly && !user?.canAccessAdmin && !canAccessAdminByRole(user?.role)) {
    return <Navigate to="/my/today" replace />
  }

  if (ownerOnly && !user?.isOwner) {
    return <Navigate to={getLcAdminHomePath(user ?? undefined)} replace />
  }

  if (requiredPermission && !hasLcPermission(user ?? undefined, requiredPermission)) {
    return <Navigate to={getLcAdminHomePath(user ?? undefined)} replace />
  }

  if (isLearnerContext && attendanceAccess.redirect) {
    return <Navigate to={attendanceAccess.redirect} replace />
  }

  // 단원평가 특강 hard lock — anchor 차례에 도달한 특강은 진단/테스트 완료 안내보다 우선한다.
  if (isLearnerContext && specialLectureFlow && 'redirect' in specialLectureFlow) {
    return <Navigate to={specialLectureFlow.redirect} replace />
  }
  if (isLearnerContext && isSpecialLectureRoute) {
    return <>{children}</>
  }

  // 학습자 흐름 우선순위: diagnosticFlow → previewGate
  if (isLearnerContext && diagnosticFlow && 'redirect' in diagnosticFlow) {
    return <Navigate to={diagnosticFlow.redirect} replace />
  }

  // 축3 프리뷰: 대기 원생은 학습 라우트 차단 → 프리뷰 홈 (usePreviewGate, fail-open)
  if (isLearnerContext && previewGate && 'redirect' in previewGate) {
    return <Navigate to={previewGate.redirect} replace />
  }

  return <>{children}</>
}

function PublicOnlyRoute({
  children,
  entry,
}: {
  children: React.ReactNode
  entry: LoginEntry
}) {
  const { isAuthenticated, hasCheckedAuth, user } = useAuthStore()
  const location = useLocation()
  const returnTo = handheldLoginReturn(new URLSearchParams(location.search).get('returnTo'))

  if (!hasCheckedAuth) return null

  if (isAuthenticated) {
    return <Navigate to={entry === 'admin' && returnTo && (user?.canAccessAdmin || canAccessAdminByRole(user?.role)) ? returnTo : resolvePostLoginPath(user?.role, entry)} replace />
  }

  return <>{children}</>
}

function App() {
  const { t } = useTranslation('coaching')
  const { initializeTenant, tenantInfo, isLoading: isTenantLoading, isLanding, notFound: isTenantNotFound } = useTenantStore()
  const { checkAuth, isHydrated, setHydrated, user } = useAuthStore()
  const hasInitialized = useRef(false)

  useEffect(() => {
    setHydrated()
  }, [setHydrated])

  useEffect(() => {
    if (!isHydrated || hasInitialized.current) return
    hasInitialized.current = true

    const initialize = async () => {
      await initializeTenant()
      // 없는 센터 주소에서는 인증 확인도 하지 않는다 (안내 화면만 보여 준다).
      if (useTenantStore.getState().notFound) return
      await checkAuth()
    }
    initialize()
  }, [isHydrated, initializeTenant, checkAuth])

  if (!isHydrated || (isTenantLoading && !tenantInfo)) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block size-8 rounded-full border-b-2 animate-spin border-primary-600"></div>
          <p className="mt-4 text-gray-600">시스템 초기화 중...</p>
        </div>
      </div>
    )
  }

  // 랜딩 페이지 (서브도메인 없음): 홈 페이지들만 노출
  if (isLanding) {
    return (
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route element={<HomeLayout />}>
            <Route path="/" element={<MainLandingPage />} />
            <Route path="/about/quebon" element={<AboutQuebonPage />} />
            <Route path="/about/lc" element={<AboutLcPage />} />
            <Route path="/franchise" element={<FranchisePage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/announcements" element={<AnnouncementsPage />} />
            <Route path="/announcements/:id" element={<AnnouncementDetailPage />} />
            <Route path="/brochure" element={<BrochurePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    )
  }

  // 등록되지 않은 센터 서브도메인: 로그인·학습 화면을 띄우지 않고 안내만 보여 준다.
  if (isTenantNotFound) {
    return <TenantNotFoundPage host={window.location.host} />
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Toaster position="top-right" richColors />
      <QuizModeProvider value={{ mode: 'student' }}>
      <AttendanceWatcher />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<PublicOnlyRoute entry="learner"><LoginPage /></PublicOnlyRoute>} />
        <Route path="/admin/login" element={<PublicOnlyRoute entry="admin"><AdminLoginPage /></PublicOnlyRoute>} />
        <Route path="/register" element={<RegisterPage />} />
        {/* 학부모 QR 자가등록 (공개, 센터 서브도메인) */}
        <Route path="/self-register" element={<SelfRegisterPage />} />

        {/* 기기 등록 전용 페이지 */}
        <Route path="/device/activate" element={<DeviceActivatePage />} />

        {/* 설명하기 사진 비로그인 모바일 업로드 (QR 진입, Phase 4c) */}
        <Route path="/photo-upload" element={<PhotoUploadPage />} />
        <Route path="/g/:token" element={<GrowthAlertPublicPage />} />
        {/* 기존에 공유된 성장 알림 URL은 7일 만료 시점까지 호환한다. */}
        <Route path="/growth-alert/:token" element={<GrowthAlertPublicPage />} />

        <Route path="/admin/m/coaching/:learnerId?" element={
          <ProtectedRoute adminOnly requiredPermission="COACHING">
            <HandheldCoachingPage />
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute adminOnly>
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<ProtectedRoute requiredPermission="DASHBOARD"><AdminDashboard /></ProtectedRoute>} />
          {/* 클래스 관리 (클래스 목록+전체 원생목록 탭) — 기존 URL은 redirect */}
          <Route path="classes" element={<ProtectedRoute requiredPermission="CLASS"><ClassManagement /></ProtectedRoute>} />
          <Route path="free-trials" element={<ProtectedRoute requiredPermission="CLASS"><TrialAnalyticsPage /></ProtectedRoute>} />
          <Route path="enrollments" element={<Navigate to="/admin/classes" replace />} />
          <Route path="timeslots" element={<Navigate to="/admin/classes" replace />} />
          <Route path="orders" element={<ProtectedRoute requiredPermission="PERFORMANCE"><OrdersPage /></ProtectedRoute>} />
          <Route path="mall/*" element={
            <MallOperationGuard>
              <ProtectedRoute requiredPermission="MALL"><MallPage /></ProtectedRoute>
            </MallOperationGuard>
          } />
          <Route path="coaching-new" element={<ProtectedRoute requiredPermission="COACHING"><TestCenterGuard><Suspense fallback={<div role="status" className="p-6 text-sm text-muted-foreground">{t('dashboard.loading')}</div>}><CoachingNewPage /></Suspense></TestCenterGuard></ProtectedRoute>} />
          <Route path="coaching-alt" element={<ProtectedRoute requiredPermission="COACHING"><TestCenterGuard><Suspense fallback={<div role="status" className="p-6 text-sm text-muted-foreground">{t('dashboard.loading')}</div>}><CoachingAltPage /></Suspense></TestCenterGuard></ProtectedRoute>} />
          <Route path="coaching" element={<ProtectedRoute requiredPermission="COACHING"><CoachingPage /></ProtectedRoute>} />
          <Route path="coaching/:learnerId" element={<ProtectedRoute requiredPermission="COACHING"><CoachingPage /></ProtectedRoute>} />
          <Route path="members" element={<ProtectedRoute requiredPermission="STUDENT"><StudentManagement /></ProtectedRoute>} />
          {import.meta.env.DEV && <Route path="products" element={<ProtectedRoute requiredPermission="CONTENT"><LcProductManagement /></ProtectedRoute>} />}
          {import.meta.env.DEV && <Route path="products/:subscriptionId" element={<ProtectedRoute requiredPermission="CONTENT"><ProductDetail /></ProtectedRoute>} />}
          {import.meta.env.DEV && <Route path="reports" element={<ProtectedRoute requiredPermission="DASHBOARD"><ProgressReport /></ProtectedRoute>} />}
          {import.meta.env.DEV && <Route path="aiq-report" element={<ProtectedRoute requiredPermission="STUDENT"><AiqOrgReport /></ProtectedRoute>} />}
          {import.meta.env.DEV && <Route path="notes" element={<ProtectedRoute requiredPermission="DASHBOARD"><NotesPage /></ProtectedRoute>} />}
          <Route path="devices" element={<ProtectedRoute requiredPermission="DEVICE"><DeviceManagement /></ProtectedRoute>} />
          <Route path="announcements" element={<ProtectedRoute requiredPermission="ANNOUNCEMENT"><AdminAnnouncements /></ProtectedRoute>} />
          <Route path="students/:userId" element={<ProtectedRoute requiredPermission="STUDENT"><StudentDetail /></ProtectedRoute>} />
          <Route path="students/:userId/aiq-report" element={<ProtectedRoute requiredPermission="STUDENT"><StudentAiqReport /></ProtectedRoute>} />
          <Route path="students/:userId/diagnostic-report" element={<ProtectedRoute requiredPermission="STUDENT"><StudentDiagnosticReport /></ProtectedRoute>} />
          <Route path="contents" element={<ProtectedRoute requiredPermission="CONTENT"><ContentManagement /></ProtectedRoute>} />
          <Route path="contents/lessons/:lessonId" element={<ProtectedRoute requiredPermission="CONTENT"><ContentLessonDetailPage /></ProtectedRoute>} />
          <Route path="entrance-tests" element={<Navigate to="/admin/members" replace />} />
          {import.meta.env.DEV && <Route path="performance" element={<ProtectedRoute requiredPermission="PERFORMANCE"><PerformanceManagement /></ProtectedRoute>} />}
          <Route path="inquiries" element={<ProtectedRoute requiredPermission="INQUIRY"><Inquiries /></ProtectedRoute>} />
          <Route path="library" element={<ProtectedRoute requiredPermission="LIBRARY"><LibraryPage /></ProtectedRoute>} />
          {import.meta.env.DEV && <Route path="settings/notifications" element={<ProtectedRoute requiredPermission="CENTER_INFO"><NotificationSettings /></ProtectedRoute>} />}
          <Route path="settings/center" element={<ProtectedRoute requiredPermission="CENTER_INFO"><CenterInfoPage /></ProtectedRoute>} />
          {/* 센터 계정 관리 (계정+역할 탭) — 기존 역할 URL은 redirect */}
          <Route path="settings/accounts" element={<ProtectedRoute ownerOnly><CenterAccountsSettings /></ProtectedRoute>} />
          <Route path="settings/roles" element={<Navigate to="/admin/settings/accounts?tab=roles" replace />} />
          <Route path="profile" element={<AdminProfile />} />
        </Route>

        {/* Admin: Game Math player (no AdminLayout — fullscreen game canvas) */}
        <Route path="/admin/game-math/:gameKey" element={
          <ProtectedRoute adminOnly>
            <ProtectedRoute requiredPermission="CONTENT"><AdminGameMathPlayer /></ProtectedRoute>
          </ProtectedRoute>
        } />

        {/* Admin: Coaching print preview (no AdminLayout — clean print canvas) */}
        <Route path="/admin/coaching/print/:learnerId/:lessonId/:kind?" element={
          <ProtectedRoute adminOnly>
            <ProtectedRoute requiredPermission="COACHING"><CoachingPrintPage /></ProtectedRoute>
          </ProtectedRoute>
        } />

        {/* Admin: 콘텐츠 관리 > 진단 테스트 레벨 테스트 세트 출력 (원생 없음, no AdminLayout) */}
        <Route path="/admin/contents/diagnostic-sets/:setId/print" element={
          <ProtectedRoute adminOnly>
            <ProtectedRoute requiredPermission="CONTENT"><DiagnosticSetPrintPage /></ProtectedRoute>
          </ProtectedRoute>
        } />

        {/* Admin: 챕터/누적 테스트 리포트 A4 인쇄 페이지 (Phase 5c) */}
        <Route path="/admin/coaching/test-report/:learnerId/:testId" element={
          <ProtectedRoute adminOnly>
            <ProtectedRoute requiredPermission="COACHING"><TestReportPrintPage /></ProtectedRoute>
          </ProtectedRoute>
        } />

        {/* Admin: 챕터 리포트 편집/인쇄 (no AdminLayout — A4 canvas) */}
        <Route path="/admin/coaching/chapter-report/:learnerId/:testId" element={
          <ProtectedRoute adminOnly>
            <ProtectedRoute requiredPermission="COACHING"><ChapterReportEditPage /></ProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/admin/coaching/chapter-report/:learnerId/:testId/print" element={
          <ProtectedRoute adminOnly>
            <ProtectedRoute requiredPermission="COACHING"><ChapterReportPrintPage /></ProtectedRoute>
          </ProtectedRoute>
        } />
        <Route path="/admin/coaching/cumulative-report/:learnerId/:assessmentId" element={
          <ProtectedRoute adminOnly>
            <ProtectedRoute requiredPermission="COACHING"><CumulativeAssessmentReportPage /></ProtectedRoute>
          </ProtectedRoute>
        } />
        {/* Learner Routes */}
        <Route path="/my" element={
          <ProtectedRoute>
            <LearnerLayout />
          </ProtectedRoute>
        }>
          <Route index element={<MyIndexRedirect />} />
          <Route path="today" element={<TodayClass />} />
          <Route path="library" element={<Library />} />
          <Route path="library/:lessonId" element={<LibraryLessonReview />} />
          <Route path="playground" element={<Playground />} />
          <Route path="support" element={<Navigate to="/my/profile" replace />} />
          <Route path="profile" element={<MyProfile />} />
          <Route path="__ds" element={<DesignSystemDemo />} />
        </Route>

        {/* 로컬 전용 화면 미리보기 — 로그인·운영 데이터 없이 상태별로 확인한다. 배포 번들에는 포함되지 않는다. */}
        {import.meta.env.DEV && (
          <Route path="/__preview/cumulative" element={<CumulativeNoticePreview />} />
        )}

        {/* Learner fullscreen learning mode (no tab bar) */}
        <Route path="/my/learn/:lessonId" element={
          <ProtectedRoute>
            <LearningLayout />
          </ProtectedRoute>
        }>
          <Route index element={<LearnPage />} />
        </Route>

        {/* Library step player (fullscreen, no tab bar) */}
        <Route path="/my/library/:lessonId/steps/:configComponentId" element={
          <ProtectedRoute>
            <LearningLayout />
          </ProtectedRoute>
        }>
          <Route index element={<LibraryStepPlayer />} />
        </Route>

        {/* Playground game player (fullscreen, no tab bar) */}
        <Route path="/playground/:gameKey" element={
          <ProtectedRoute>
            <PlaygroundGame />
          </ProtectedRoute>
        } />

        {/* Full-screen Learner Routes (no tab bar) */}
        <Route path="/aiq-test" element={
          <ProtectedRoute>
            <AiqTest />
          </ProtectedRoute>
        } />

        {/* Diagnostic flow (진단테스트 — 풀스크린, 헤더/탭바 미노출) */}
        <Route path="/my/diagnostic/:diagnosticId/aiq" element={
          <ProtectedRoute>
            <DiagnosticAiq />
          </ProtectedRoute>
        } />
        <Route path="/my/diagnostic/:diagnosticId/aiq-complete" element={
          <ProtectedRoute>
            <DiagnosticAiqComplete />
          </ProtectedRoute>
        } />

        <Route path="/my/cumulative-assessments/:assessmentId/math-head" element={
          <ProtectedRoute>
            <MathHeadAssessment />
          </ProtectedRoute>
        } />

        {/* 단원평가 특강 (Special Lecture) */}
        <Route path="/my/special-lecture/:id/online" element={
          <ProtectedRoute>
            <SpecialLectureOnline />
          </ProtectedRoute>
        } />
        <Route path="/my/special-lecture/:id/review" element={
          <ProtectedRoute>
            <SpecialLectureReview />
          </ProtectedRoute>
        } />
        <Route path="/my/special-lecture/:id/wait" element={
          <ProtectedRoute>
            <SpecialLectureWait />
          </ProtectedRoute>
        } />

        {/* Dev Tools */}
        {isDevToolsVisible(user?.email) && (
          <>
          <Route path="/test/quiz" element={
            <ProtectedRoute>
              <QuizTestPage />
            </ProtectedRoute>
          } />
          <Route path="/test/aiq" element={
            <ProtectedRoute>
              <AiqTestPage />
            </ProtectedRoute>
          } />
          <Route path="/test/tool" element={
            <ProtectedRoute>
              <ToolTestPage />
            </ProtectedRoute>
          } />
          <Route path="/test/queture" element={
            <ProtectedRoute>
              <QuetureTestPage />
            </ProtectedRoute>
          } />
          <Route path="/test/game" element={
            <ProtectedRoute>
              <GameTestPage />
            </ProtectedRoute>
          } />
          </>
        )}

        {/* Default Redirect */}
        <Route path="/" element={<Navigate to="/admin" replace />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
      </QuizModeProvider>
    </BrowserRouter>
  )
}

export default App
